import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';

export interface StepHeaderProps {
  /** Current step, 1-based. */
  step: number;
  /** Number of steps in this flow. */
  total: number;
  /** The one question this screen asks ("Where should we pick you up?"). Rendered as the page h1. */
  title: ReactNode;
  /** One or two short sentences under the title. */
  lede?: ReactNode;
  /** Where Back goes. Omit to go back in history; pass `onBack` to handle it yourself. */
  backTo?: string;
  onBack?: () => void;
  /** Defaults to "Back". */
  backLabel?: string;
  /** Hide the Back link (first step that is also a tab root). */
  hideBack?: boolean;
  /** Earlier choices as small chips ("Sun 12 Oct", "Glen Austin"), so members can see what they picked. */
  chips?: ReactNode[];
}

/**
 * Top of every step in a flow: a 48 px Back link and "Step 2 of 4" on one row, a progress bar,
 * the title (h1), an optional lede and chips for the choices made so far.
 */
export function StepHeader({ step, total, title, lede, backTo, onBack, backLabel = 'Back', hideBack, chips }: StepHeaderProps) {
  const navigate = useNavigate();
  const safeTotal = Math.max(1, total);
  const now = Math.min(Math.max(1, step), safeTotal);
  const pct = Math.round((now / safeTotal) * 100);
  const back = () => {
    if (onBack) onBack();
    else if (backTo) navigate(backTo);
    else navigate(-1);
  };
  return (
    <header className="rides-stephead">
      <div className="top">
        {hideBack ? <span /> : (
          <button type="button" className="rides-backlink" onClick={back}>
            <IconArrowLeft size={20} aria-hidden="true" /> {backLabel}
          </button>
        )}
        <span className="count">Step {now} of {safeTotal}</span>
      </div>
      <div
        className="rides-progress"
        role="progressbar"
        aria-label={`Step ${now} of ${safeTotal}`}
        aria-valuemin={1}
        aria-valuemax={safeTotal}
        aria-valuenow={now}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      <h1 className="rides-title">{title}</h1>
      {lede && <p className="rides-lede">{lede}</p>}
      {chips && chips.filter(Boolean).length > 0 && (
        <div className="chips" aria-label="Your choices so far">
          {chips.filter(Boolean).map((c, i) => <span key={i}>{c}</span>)}
        </div>
      )}
    </header>
  );
}
